import { z } from 'zod';
import { CodingSchema, IsoDate, IsoDateTime, ProvenanceSchema } from './common.js';

const Base = z.object({
  tenantId: z.string(),
  patientId: z.string(),
  recordedByUserId: z.string(),
  recordedAt: IsoDateTime,
  provenance: ProvenanceSchema.default('clinician-entered'),
  encounterId: z.string().optional(),
  updatedAt: IsoDateTime,
});

// ---------------------------------------------------------------- Encounters
export const EncounterStatusSchema = z.enum(['PLANNED', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED', 'CANCELLED']);
export const EncounterTypeSchema = z.enum(['OFFICE_VISIT', 'FOLLOW_UP', 'TELEPHONE', 'VIDEO', 'PROCEDURE', 'NURSE_VISIT', 'ADMIN_REVIEW']);

export const EncounterSchema = z.object({
  tenantId: z.string(),
  patientId: z.string(),
  encounterId: z.string(),
  appointmentId: z.string().optional(),
  providerId: z.string(),
  type: EncounterTypeSchema,
  status: EncounterStatusSchema,
  startedAt: IsoDateTime,
  endedAt: IsoDateTime.optional(),
  chiefComplaint: z.string().optional(),
  historyOfPresentIllness: z.string().optional(),
  reviewOfSystems: z.string().optional(),
  physicalExam: z.string().optional(),
  assessment: z.string().optional(),
  plan: z.string().optional(),
  followUpInstructions: z.string().optional(),
  createdByUserId: z.string(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  version: z.number().int().default(1),
});
export type Encounter = z.infer<typeof EncounterSchema>;

export const DiagnosisSchema = Base.extend({
  diagnosisId: z.string(),
  code: CodingSchema.describe('ICD-10 or SNOMED CT'),
  description: z.string(),
  rank: z.enum(['PRIMARY', 'SECONDARY']).default('SECONDARY'),
  status: z.enum(['ACTIVE', 'RESOLVED', 'RULED_OUT']).default('ACTIVE'),
  onsetDate: IsoDate.optional(),
});
export type Diagnosis = z.infer<typeof DiagnosisSchema>;

export const ProblemSchema = Base.extend({
  problemId: z.string(),
  code: CodingSchema.optional(),
  description: z.string().min(1),
  status: z.enum(['ACTIVE', 'INACTIVE', 'RESOLVED']).default('ACTIVE'),
  onsetDate: IsoDate.optional(),
  resolvedDate: IsoDate.optional(),
  severity: z.enum(['MILD', 'MODERATE', 'SEVERE']).optional(),
});
export type Problem = z.infer<typeof ProblemSchema>;

export const AllergySchema = Base.extend({
  allergyId: z.string(),
  substance: z.string().min(1).describe('Display name, e.g. Penicillin'),
  substanceCode: CodingSchema.optional().describe('RxNorm ingredient or SNOMED substance'),
  category: z.enum(['MEDICATION', 'FOOD', 'ENVIRONMENT', 'OTHER']),
  reaction: z.string().optional(),
  severity: z.enum(['MILD', 'MODERATE', 'SEVERE', 'LIFE_THREATENING']).optional(),
  status: z.enum(['ACTIVE', 'INACTIVE', 'REFUTED']).default('ACTIVE'),
  verified: z.boolean().default(false),
});
export type Allergy = z.infer<typeof AllergySchema>;

export const HistoryCategorySchema = z.enum(['MEDICAL', 'SURGICAL', 'FAMILY', 'SOCIAL']);
export const HistoryEntrySchema = Base.extend({
  historyId: z.string(),
  category: HistoryCategorySchema,
  description: z.string().min(1),
  code: CodingSchema.optional(),
  date: IsoDate.optional(),
  /** FAMILY: relationship of relative; SOCIAL: e.g. smoking, alcohol, occupation. */
  qualifier: z.string().optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE'),
});
export type HistoryEntry = z.infer<typeof HistoryEntrySchema>;

export const ImmunizationSchema = Base.extend({
  immunizationId: z.string(),
  vaccine: z.string(),
  vaccineCode: CodingSchema.optional(),
  date: IsoDate,
  lotNumber: z.string().optional(),
  doseNumber: z.number().int().optional(),
});
export type Immunization = z.infer<typeof ImmunizationSchema>;

// ---------------------------------------------------------------- Medications
export const MedicationCatalogItemSchema = z.object({
  medicationCode: z.string().describe('Catalog key, RxNorm RXCUI where known'),
  name: z.string(),
  nameAr: z.string().optional(),
  ingredient: z.string(),
  ingredientCode: CodingSchema.optional(),
  /** Pharmacologic class tokens used by duplicate-therapy and allergy cross-checks (e.g. PENICILLIN, NSAID). */
  classes: z.array(z.string()).default([]),
  form: z.string().optional(),
  strength: z.string().optional(),
  routes: z.array(z.string()).default([]),
  controlled: z.boolean().default(false),
  /** Synthetic catalog entries are flagged so no one mistakes them for a licensed formulary. */
  source: z.enum(['SYNTHETIC_DEMO', 'TENANT_FORMULARY', 'LICENSED']).default('SYNTHETIC_DEMO'),
});
export type MedicationCatalogItem = z.infer<typeof MedicationCatalogItemSchema>;

export const DosageSchema = z.object({
  dose: z.string().min(1).describe('e.g. 500 mg'),
  route: z.string().min(1).describe('e.g. oral'),
  frequency: z.string().min(1).describe('e.g. twice daily'),
  durationDays: z.number().int().min(1).max(365).optional(),
  quantity: z.number().int().min(1).optional(),
  refills: z.number().int().min(0).max(12).default(0),
  instructions: z.string().optional(),
  prn: z.boolean().default(false),
});
export type Dosage = z.infer<typeof DosageSchema>;

/** A patient's medication list entry (what they are taking), regardless of who prescribed it. */
export const MedicationSchema = Base.extend({
  medicationId: z.string(),
  medicationCode: z.string().optional(),
  name: z.string().min(1),
  ingredient: z.string().optional(),
  classes: z.array(z.string()).default([]),
  dosage: DosageSchema.partial(),
  status: z.enum(['ACTIVE', 'COMPLETED', 'STOPPED', 'ON_HOLD']).default('ACTIVE'),
  startDate: IsoDate.optional(),
  endDate: IsoDate.optional(),
  prescriptionId: z.string().optional(),
  reconciledAt: IsoDateTime.optional(),
});
export type Medication = z.infer<typeof MedicationSchema>;

export const PrescriptionStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'COMPLETED', 'CANCELLED', 'ON_HOLD']);
export const PrescriptionSchema = Base.extend({
  prescriptionId: z.string(),
  medicationCode: z.string().optional(),
  medicationName: z.string().min(1),
  ingredient: z.string().optional(),
  classes: z.array(z.string()).default([]),
  dosage: DosageSchema,
  status: PrescriptionStatusSchema,
  prescriberId: z.string(),
  /** Synthetic-data MVP: prescriptions are simulated and never transmitted to a pharmacy. */
  simulated: z.boolean().default(true),
  idempotencyKey: z.string(),
  cdsAcknowledgedFindingIds: z.array(z.string()).default([]),
});
export type Prescription = z.infer<typeof PrescriptionSchema>;

// ---------------------------------------------------------------- Orders & results
export const OrderCategorySchema = z.enum(['LAB', 'RADIOLOGY']);
export const OrderStatusSchema = z.enum(['DRAFT', 'ORDERED', 'IN_PROGRESS', 'RESULTED', 'CANCELLED']);

export const OrderItemSchema = z.object({
  code: CodingSchema.describe('LOINC for labs, local/SNOMED for imaging'),
  display: z.string(),
  specimen: z.string().optional(),
  bodySite: z.string().optional(),
  modality: z.string().optional(),
  priority: z.enum(['ROUTINE', 'URGENT', 'STAT']).default('ROUTINE'),
});
export type OrderItem = z.infer<typeof OrderItemSchema>;

export const ClinicalOrderSchema = Base.extend({
  orderId: z.string(),
  category: OrderCategorySchema,
  items: z.array(OrderItemSchema).min(1),
  status: OrderStatusSchema,
  ordererId: z.string(),
  clinicalIndication: z.string().optional(),
  idempotencyKey: z.string(),
  cdsAcknowledgedFindingIds: z.array(z.string()).default([]),
  resultedAt: IsoDateTime.optional(),
});
export type ClinicalOrder = z.infer<typeof ClinicalOrderSchema>;

export const ReferenceRangeSchema = z.object({
  low: z.number().optional(),
  high: z.number().optional(),
  text: z.string().optional(),
});

export const AbnormalFlagSchema = z.enum(['L', 'H', 'LL', 'HH', 'A', 'N']);

/** One laboratory observation. Sort key is code + effective time so trends are a single range query. */
export const LabResultSchema = Base.extend({
  resultId: z.string(),
  orderId: z.string().optional(),
  code: CodingSchema,
  display: z.string(),
  value: z.number().optional(),
  valueText: z.string().optional(),
  unit: z.string().optional(),
  referenceRange: ReferenceRangeSchema.optional(),
  flag: AbnormalFlagSchema.default('N'),
  status: z.enum(['PRELIMINARY', 'FINAL', 'CORRECTED', 'CANCELLED']).default('FINAL'),
  collectedAt: IsoDateTime,
  resultedAt: IsoDateTime,
  performer: z.string().optional(),
});
export type LabResult = z.infer<typeof LabResultSchema>;

export const RadiologyReportSchema = Base.extend({
  reportId: z.string(),
  orderId: z.string().optional(),
  modality: z.string(),
  procedure: z.string(),
  procedureCode: CodingSchema.optional(),
  status: z.enum(['PRELIMINARY', 'FINAL', 'ADDENDUM']).default('FINAL'),
  findings: z.string(),
  impression: z.string(),
  performedAt: IsoDateTime,
  reportedAt: IsoDateTime,
  radiologist: z.string().optional(),
  attachmentDocumentIds: z.array(z.string()).default([]),
});
export type RadiologyReport = z.infer<typeof RadiologyReportSchema>;

export const VitalSignsSchema = Base.extend({
  vitalsId: z.string(),
  effectiveAt: IsoDateTime,
  systolicMmHg: z.number().optional(),
  diastolicMmHg: z.number().optional(),
  heartRateBpm: z.number().optional(),
  respiratoryRate: z.number().optional(),
  temperatureC: z.number().optional(),
  spo2Percent: z.number().optional(),
  weightKg: z.number().optional(),
  heightCm: z.number().optional(),
  bmi: z.number().optional(),
  painScore: z.number().int().min(0).max(10).optional(),
});
export type VitalSigns = z.infer<typeof VitalSignsSchema>;

export const ClinicalNoteSchema = Base.extend({
  noteId: z.string(),
  type: z.enum(['PROGRESS', 'SOAP', 'CONSULT', 'PROCEDURE', 'TELEPHONE', 'ADDENDUM', 'OTHER']).default('PROGRESS'),
  title: z.string().optional(),
  /** Markdown-safe plain text. Never AI-authored without a clinician instruction; provenance records that. */
  body: z.string().min(1),
  status: z.enum(['DRAFT', 'SIGNED', 'AMENDED']).default('DRAFT'),
  authorId: z.string(),
  signedAt: IsoDateTime.optional(),
  idempotencyKey: z.string().optional(),
});
export type ClinicalNote = z.infer<typeof ClinicalNoteSchema>;

export const DocumentSchema = Base.extend({
  documentId: z.string(),
  title: z.string(),
  category: z.enum(['SCAN', 'IMAGE', 'REPORT', 'CONSENT', 'REFERRAL', 'OTHER']),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  /** S3 object key; bucket is tenant-agnostic and keyed by tenant prefix. Never a public URL. */
  s3Key: z.string(),
  sha256: z.string().optional(),
  status: z.enum(['PENDING_UPLOAD', 'AVAILABLE', 'DELETED']).default('PENDING_UPLOAD'),
});
export type ClinicalDocument = z.infer<typeof DocumentSchema>;

export const QuestionnaireResponseSchema = Base.extend({
  responseId: z.string(),
  questionnaireId: z.string(),
  answers: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.array(z.string())])),
  completedAt: IsoDateTime,
});
export type QuestionnaireResponse = z.infer<typeof QuestionnaireResponseSchema>;
